/* =========================================================================
   EGONEX — Header davranışları
   - Sabit (sticky) + kaydırınca küçülme
   - Mega menü (hover / tıklama)
   - Daktilo efektli arama + kategori seçici + sesli arama
   - Menü çekmecesi
   - Duyuru barı slider modu
   - Açık / koyu tema
   ========================================================================= */
(function () {
  'use strict';

  const prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  const store = {
    get(key) {
      try {
        return window.localStorage.getItem(key);
      } catch (e) {
        return null;
      }
    },
    set(key, value) {
      try {
        window.localStorage.setItem(key, value);
      } catch (e) {
        /* sessiz geç */
      }
    },
  };

  /* ---------------------------------------------------------------- HEADER */
  class EgonexHeader extends HTMLElement {
    connectedCallback() {
      this.stickyEl = this.querySelector('.eg-header__sticky');
      this.mode = this.dataset.sticky || 'none';
      this.shrink = this.dataset.shrink === 'true';
      this.megaTrigger = this.dataset.megaTrigger || 'hover';
      this.lastY = window.scrollY;
      this.ticking = false;

      this.measure();
      this.setupMega();
      this.setupTheme();

      if (this.mode !== 'none') {
        this.onScroll = this.onScroll.bind(this);
        window.addEventListener('scroll', this.onScroll, { passive: true });
      }

      this.resizeObserver = new ResizeObserver(() => this.measure());
      if (this.stickyEl) this.resizeObserver.observe(this.stickyEl);

      // Tema editöründe bölüm yeniden yüklenirse ölçümü tazele
      document.addEventListener('shopify:section:load', () => this.measure());
    }

    disconnectedCallback() {
      window.removeEventListener('scroll', this.onScroll);
      this.resizeObserver?.disconnect();
    }

    measure() {
      if (!this.stickyEl) return;
      // Sabitlenmişken ölçme — küçülen header yer tutucuyu zıplatmasın
      if (this.classList.contains('is-sticky')) return;
      const h = this.stickyEl.offsetHeight;
      this.style.setProperty('--eg-header-h', h + 'px');
      document.documentElement.style.setProperty('--eg-header-height', h + 'px');
      this.offsetTopValue = this.offsetTop;
    }

    onScroll() {
      if (this.ticking) return;
      this.ticking = true;
      window.requestAnimationFrame(() => {
        const y = window.scrollY;
        const trigger = this.offsetTopValue || 0;

        if (y > trigger + 4) {
          this.classList.add('is-sticky');
          if (this.shrink) this.classList.add('is-shrunk');
        } else {
          this.classList.remove('is-sticky', 'is-shrunk', 'is-hidden');
        }

        if (this.mode === 'scroll-up' && this.classList.contains('is-sticky')) {
          const goingDown = y > this.lastY;
          if (goingDown && y > trigger + 160 && !this.hasOpenMenu()) {
            this.classList.add('is-hidden');
          } else {
            this.classList.remove('is-hidden');
          }
        }

        this.lastY = y;
        this.ticking = false;
      });
    }

    hasOpenMenu() {
      return !!this.querySelector('.eg-nav__item.is-open');
    }

    /* --- Mega menü --- */
    setupMega() {
      const items = this.querySelectorAll('.eg-nav__item.has-mega');
      if (!items.length) return;

      const closeAll = (except) => {
        items.forEach((i) => {
          if (i !== except) {
            i.classList.remove('is-open');
            i.querySelector('.eg-nav__link')?.setAttribute('aria-expanded', 'false');
          }
        });
      };

      items.forEach((item) => {
        const link = item.querySelector('.eg-nav__link');

        if (this.megaTrigger === 'hover') {
          let openTimer, closeTimer;
          item.addEventListener('mouseenter', () => {
            clearTimeout(closeTimer);
            openTimer = setTimeout(() => {
              closeAll(item);
              item.classList.add('is-open');
              link?.setAttribute('aria-expanded', 'true');
            }, prefersReduced ? 0 : 90);
          });
          item.addEventListener('mouseleave', () => {
            clearTimeout(openTimer);
            closeTimer = setTimeout(() => {
              item.classList.remove('is-open');
              link?.setAttribute('aria-expanded', 'false');
            }, 140);
          });
        } else {
          link?.addEventListener('click', (e) => {
            e.preventDefault();
            const isOpen = item.classList.contains('is-open');
            closeAll();
            item.classList.toggle('is-open', !isOpen);
            link.setAttribute('aria-expanded', String(!isOpen));
          });
        }

        // Klavye erişimi
        item.addEventListener('focusin', () => {
          closeAll(item);
          item.classList.add('is-open');
        });
        item.addEventListener('focusout', (e) => {
          if (!item.contains(e.relatedTarget)) item.classList.remove('is-open');
        });
      });

      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') closeAll();
      });
    }

    /* --- Açık / koyu tema --- */
    setupTheme() {
      const btn = this.querySelector('[data-eg-theme-toggle]');
      if (!btn) return;
      btn.addEventListener('click', () => {
        const current = document.documentElement.getAttribute('data-eg-theme') === 'dark' ? 'dark' : 'light';
        const next = current === 'dark' ? 'light' : 'dark';
        document.documentElement.setAttribute('data-eg-theme', next);
        store.set('eg-theme', next);
      });
    }
  }
  if (!customElements.get('egonex-header')) customElements.define('egonex-header', EgonexHeader);

  // Sayfa açılışında kayıtlı temayı uygula (FOUC'u azaltmak için hemen)
  const savedTheme = store.get('eg-theme');
  if (savedTheme) document.documentElement.setAttribute('data-eg-theme', savedTheme);

  /* ---------------------------------------------------------------- ARAMA */
  class EgonexSearch extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector('.eg-search__input');
      this.form = this.querySelector('form');
      if (!this.input || !this.form) return;

      this.setupCategories();
      this.setupMic();
      this.setupTypewriter();
    }

    disconnectedCallback() {
      this.stopTypewriter();
    }

    /* Kategori açılır listesi */
    setupCategories() {
      const toggle = this.querySelector('[data-eg-cat-toggle]');
      const menu = this.querySelector('.eg-search__cat-menu');
      if (!toggle || !menu) return;

      const label = this.querySelector('[data-eg-cat-label]');
      const valueField = this.querySelector('[data-eg-cat-value]');
      const urlField = this.querySelector('[data-eg-cat-url]');

      const close = () => {
        menu.hidden = true;
        toggle.setAttribute('aria-expanded', 'false');
      };

      toggle.addEventListener('click', (e) => {
        e.stopPropagation();
        const open = menu.hidden;
        menu.hidden = !open;
        toggle.setAttribute('aria-expanded', String(open));
      });

      menu.querySelectorAll('[data-eg-cat-item]').forEach((btn) => {
        btn.addEventListener('click', () => {
          menu.querySelectorAll('[data-eg-cat-item]').forEach((b) => b.classList.remove('is-selected'));
          btn.classList.add('is-selected');
          if (label) label.textContent = btn.textContent.trim();
          if (valueField) valueField.value = btn.dataset.value || '';
          if (urlField) urlField.value = btn.dataset.url || '';
          close();
          this.input.focus();
        });
      });

      document.addEventListener('click', (e) => {
        if (!this.contains(e.target)) close();
      });

      this.form.addEventListener('submit', (e) => {
        const category = valueField?.value || '';
        const url = urlField?.value || '';
        if (!category) return;

        const mode = this.dataset.catMode || 'keyword';
        const term = this.input.value.trim();

        if (mode === 'collection' || !term) {
          if (url) {
            e.preventDefault();
            window.location.href = term ? `${url}?q=${encodeURIComponent(term)}` : url;
          }
          return;
        }
        // Kategoriyi arama kelimesine ekle
        if (!term.toLowerCase().includes(category.toLowerCase())) {
          this.input.value = `${term} ${category}`;
        }
      });
    }

    /* Sesli arama */
    setupMic() {
      const mic = this.querySelector('[data-eg-mic]');
      if (!mic) return;
      const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
      if (!SR) {
        mic.remove();
        return;
      }

      const recognition = new SR();
      recognition.lang = document.documentElement.lang || 'tr-TR';
      recognition.interimResults = false;
      recognition.maxAlternatives = 1;

      mic.addEventListener('click', () => {
        try {
          recognition.start();
          mic.classList.add('is-listening');
        } catch (err) {
          /* zaten çalışıyor */
        }
      });

      recognition.addEventListener('result', (event) => {
        const transcript = event.results[0][0].transcript;
        this.input.value = transcript;
        this.input.dispatchEvent(new Event('input', { bubbles: true }));
        this.form.requestSubmit ? this.form.requestSubmit() : this.form.submit();
      });

      ['end', 'error'].forEach((evt) =>
        recognition.addEventListener(evt, () => mic.classList.remove('is-listening'))
      );
    }

    /* Daktilo efektli placeholder */
    setupTypewriter() {
      if (this.dataset.typewriter !== 'true' || prefersReduced) return;
      const terms = (this.dataset.terms || '')
        .split('|')
        .map((t) => t.trim())
        .filter(Boolean);
      if (!terms.length) return;

      this.terms = terms;
      this.prefix = this.dataset.prefix || '';
      this.typeSpeed = parseInt(this.dataset.typeSpeed, 10) || 90;
      this.deleteSpeed = parseInt(this.dataset.deleteSpeed, 10) || 45;
      this.holdTime = parseInt(this.dataset.holdTime, 10) || 1600;
      this.termIndex = 0;
      this.charIndex = 0;
      this.deleting = false;
      this.paused = false;

      this.input.addEventListener('focus', () => {
        this.paused = true;
        this.input.placeholder = this.prefix;
      });
      this.input.addEventListener('blur', () => {
        if (!this.input.value) {
          this.paused = false;
          this.tick();
        }
      });

      // Görünür değilse boşuna çalışmasın
      this.observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          this.offscreen = !entry.isIntersecting;
          if (entry.isIntersecting && !this.timer) this.tick();
        });
      });
      this.observer.observe(this);

      this.tick();
    }

    tick() {
      clearTimeout(this.timer);
      if (this.paused || this.offscreen || !this.terms) return;

      const term = this.terms[this.termIndex];
      let delay;

      if (this.deleting) {
        this.charIndex -= 1;
        delay = this.deleteSpeed;
      } else {
        this.charIndex += 1;
        delay = this.typeSpeed;
      }

      this.input.placeholder = term.slice(0, this.charIndex) + '|';

      if (!this.deleting && this.charIndex >= term.length) {
        this.deleting = true;
        delay = this.holdTime;
        this.input.placeholder = term;
      } else if (this.deleting && this.charIndex <= 0) {
        this.deleting = false;
        this.termIndex = (this.termIndex + 1) % this.terms.length;
        delay = 320;
      }

      this.timer = setTimeout(() => this.tick(), delay);
    }

    stopTypewriter() {
      clearTimeout(this.timer);
      this.observer?.disconnect();
    }
  }
  if (!customElements.get('egonex-search')) customElements.define('egonex-search', EgonexSearch);

  /* --------------------------------------------------------- MENÜ ÇEKMECE */
  class EgonexNavDrawer extends HTMLElement {
    connectedCallback() {
      this.panel = this.querySelector('.eg-navdrawer__panel');

      document.addEventListener('click', (e) => {
        const opener = e.target.closest('[data-eg-drawer-open]');
        if (opener) {
          e.preventDefault();
          this.open();
        }
      });

      this.querySelectorAll('[data-eg-drawer-close]').forEach((el) =>
        el.addEventListener('click', () => this.close())
      );

      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && !this.hidden) this.close();
      });

      this.querySelectorAll('[data-eg-acc-toggle]').forEach((btn) => {
        btn.addEventListener('click', () => {
          const sub = btn.nextElementSibling;
          const isOpen = btn.getAttribute('aria-expanded') === 'true';
          btn.setAttribute('aria-expanded', String(!isOpen));
          if (sub) sub.hidden = isOpen;
        });
      });
    }

    open() {
      this.hidden = false;
      document.body.classList.add('eg-locked');
      requestAnimationFrame(() => this.classList.add('is-open'));
      setTimeout(() => this.querySelector('.eg-navdrawer__close')?.focus(), 220);
      document.dispatchEvent(new CustomEvent('eg:overlay:open', { detail: { source: 'nav' } }));
    }

    close() {
      this.classList.remove('is-open');
      document.body.classList.remove('eg-locked');
      setTimeout(() => {
        this.hidden = true;
      }, 340);
      document.dispatchEvent(new CustomEvent('eg:overlay:close', { detail: { source: 'nav' } }));
    }
  }
  if (!customElements.get('egonex-nav-drawer')) customElements.define('egonex-nav-drawer', EgonexNavDrawer);

  /* ------------------------------------------------------------- DUYURU */
  class EgonexAnnouncement extends HTMLElement {
    connectedCallback() {
      if (this.dataset.mode !== 'slider') return;
      this.items = Array.from(this.querySelectorAll('.eg-announce__item'));
      if (this.items.length < 2) {
        this.items[0]?.classList.add('is-current');
        return;
      }
      this.index = 0;
      this.interval = parseInt(this.dataset.interval, 10) || 4000;
      this.items[0].classList.add('is-current');

      this.querySelector('[data-eg-announce-next]')?.addEventListener('click', () => this.go(1));
      this.querySelector('[data-eg-announce-prev]')?.addEventListener('click', () => this.go(-1));

      if (this.dataset.pause === 'true') {
        this.addEventListener('mouseenter', () => this.stop());
        this.addEventListener('mouseleave', () => this.start());
      }
      this.start();
    }

    disconnectedCallback() {
      this.stop();
    }

    start() {
      if (prefersReduced) return;
      this.stop();
      this.timer = setInterval(() => this.go(1), this.interval);
    }

    stop() {
      clearInterval(this.timer);
    }

    go(dir) {
      const prev = this.items[this.index];
      this.index = (this.index + dir + this.items.length) % this.items.length;
      const next = this.items[this.index];

      prev.classList.add('is-leaving');
      prev.classList.remove('is-current');
      setTimeout(() => prev.classList.remove('is-leaving'), 500);
      next.classList.add('is-current');
    }
  }
  if (!customElements.get('egonex-announcement')) customElements.define('egonex-announcement', EgonexAnnouncement);
})();
